import { readSetting, removeAndQueueSave, writeAndQueueSave } from "@features/office";

import {
    SETTINGS_PAYLOAD_VERSION,
    type Assignment,
    type AssignmentMap,
    type ElementSetting,
    type ElementSettingMap,
    type SettingsPayload,
    type SlideSetting,
    type SlideSettingMap,
} from "../SettingsPayload";
import { migrateV6, type LegacyV6Payload } from "../helpers/migrateV6";
import { migrateToUids, type UidMapping } from "../helpers/migrateToUids";

import type { GenerationSettingStore } from "./GenerationSettingStoreContract";

/**
 * {@link GenerationSettingStore} の唯一の実装。`Office.context.document.settings` の
 * `im.prompts` を真実とし、書き込みはすべて `writeAndQueueSave` 経由で永続化キューへ入る。
 *
 * 読み込みは版で分岐する。v8 はそのまま、v6 以下は {@link migrateV6} を通す。現行より新しい版は
 * 読取専用退避して破壊上書きを防ぐ。旧 `im.shapePrompts`（暗黙 v1）は v6 の形へ読み替えてから
 * 同じ経路を通す。
 *
 * v7 以下は鍵が PowerPoint 発行の ID なので、**この場では v8 にできない**。新しい鍵は走査
 * （`reconcileUids`）が握っており、それは非同期だからで、対応表が届くまで旧版のまま保持する。
 * {@link SettingsGenerationSettingStore.applyUidMapping} がその受け口。届かない間の書き込みは
 * 旧版の見出しで永続化する。v8 と偽ると、次に読んだとき鍵の意味を取り違える。
 */

const SETTINGS_KEY = "im.prompts";
const LEGACY_KEY = "im.shapePrompts";

type Cache = {
    slides: SlideSettingMap;
    elements: ElementSettingMap;
    assignments: AssignmentMap;
};

function emptyCache(): Cache {
    return { slides: {}, elements: {}, assignments: {} };
}

/** 何も書かれていない担当か。ラベル・本文・対象のいずれかが入っていれば下書きではない。 */
function isAssignmentBlank(a: Assignment): boolean {
    return (
        a.label.trim().length === 0 && a.instruction.trim().length === 0 && a.targets.length === 0
    );
}

/** 旧 `im.shapePrompts`（暗黙 v1）を v6 の形へ読み替える。移行の入口を 1 本にするため。 */
function legacyV1ToV6(legacy: {
    [shapeId: string]: { prompt: string; label?: string; updatedAt: string; updatedBy: string };
}): LegacyV6Payload {
    const prompts: NonNullable<LegacyV6Payload["prompts"]> = {};
    for (const [shapeId, sp] of Object.entries(legacy)) {
        prompts[globalThis.crypto.randomUUID()] = {
            label: sp.label,
            prompt: sp.prompt,
            targets: [{ ref: `element:${shapeId}`, role: "output" }],
            updatedAt: sp.updatedAt,
            updatedBy: sp.updatedBy,
        };
    }
    return { version: 6, prompts };
}

/**
 * 廃止した裁量 `auto` を `embed` として読む。
 *
 * `auto` は「未設定＝枠の種別から導出する」を表す位置だったが、導出は実装されないまま選択肢
 * だけが残っていた。既定を `embed` に定めたので、保存済みの `auto` はここで読み替える。
 * ファイル側は書き換えない（次にその設定を保存したときに `embed` として残る）。
 *
 * 一度も裁量を触っていない要素は未設定のまま残す。「作成者が埋め込みと決めた」と「何も決めて
 * いない」の区別は、後で使う側が両者を分けたくなったときに要るため。
 */
function readAutoAsEmbed(elements: ElementSettingMap): ElementSettingMap {
    let changed = false;
    const out: ElementSettingMap = {};
    for (const [id, setting] of Object.entries(elements)) {
        // 保存済みの値は現行の型に無いので、文字列として見る。
        if ((setting.discretion as string | undefined) === "auto") {
            out[id] = { ...setting, discretion: "embed" };
            changed = true;
        } else {
            out[id] = setting;
        }
    }
    return changed ? out : elements;
}

/**
 * 0 以上の整数でない `maxRowsBeforeNext` を読み捨てて「自動」へ戻す。
 *
 * 組立側（lambda）も同じ条件で読み捨てる。ここで落としておくと、画面に出る値と組立が使う値が
 * 食い違わない。ファイル側は書き換えない（次にその設定を保存したときに消える）。
 */
function dropInvalidMaxRowsBeforeNext(elements: ElementSettingMap): ElementSettingMap {
    let changed = false;
    const out: ElementSettingMap = {};
    for (const [id, setting] of Object.entries(elements)) {
        // 保存済みの値は型を信じられないので、unknown として見る。
        const value = setting.maxRowsBeforeNext as unknown;
        if (value !== undefined && !(Number.isInteger(value) && (value as number) >= 0)) {
            const { maxRowsBeforeNext: _dropped, ...rest } = setting;
            out[id] = rest;
            changed = true;
        } else {
            out[id] = setting;
        }
    }
    return changed ? out : elements;
}

export class SettingsGenerationSettingStore implements GenerationSettingStore {
    private cache: Cache = emptyCache();
    private loaded = false;
    /** いま保持しているデータの版。移行が済むまで現行版に上げない。 */
    private version = SETTINGS_PAYLOAD_VERSION;
    private readOnly = false;
    private _sourceTaskId: string | null = null;
    private _sourceProjectId: string | null = null;

    private slidesListeners = new Set<() => void>();
    private slideListenersMap = new Map<string, Set<() => void>>();
    private elementsListeners = new Set<() => void>();
    private elementListenersMap = new Map<string, Set<() => void>>();
    private assignmentIdsListeners = new Set<() => void>();
    private assignmentsListeners = new Set<() => void>();
    private assignmentListenersMap = new Map<string, Set<() => void>>();

    /**
     * id 配列は getSnapshot で参照安定が必須（毎回 Object.keys を返すと無限再レンダーになる）。
     * id 集合が変わったときだけ作り直す。
     */
    private cachedSlideIds: string[] = [];
    private cachedAssignmentIds: string[] = [];

    isReadOnly(): boolean {
        this.ensureLoaded();
        return this.readOnly;
    }

    needsUidMigration(): boolean {
        this.ensureLoaded();
        return !this.readOnly && this.version < SETTINGS_PAYLOAD_VERSION;
    }

    /**
     * 旧鍵（PowerPoint 発行の ID）から新鍵（アドイン発行の識別子）へ移す。
     *
     * 走査が対応表を握っている瞬間にしか呼べないため、読み込みとは別の入口にしてある。
     * 二度目以降は何もしない。移行後は全購読へ通知する。鍵が総入れ替えになるので、
     * 個別の id 購読では拾えない。
     */
    applyUidMapping(mapping: UidMapping): void {
        this.ensureLoaded();
        if (this.readOnly || this.version >= SETTINGS_PAYLOAD_VERSION) return;
        const next = migrateToUids(this.buildPayload(), mapping);
        this.version = SETTINGS_PAYLOAD_VERSION;
        this.cache = {
            slides: next.slides,
            elements: next.elements,
            assignments: next.assignments,
        };
        this.cachedSlideIds = Object.keys(this.cache.slides);
        this.cachedAssignmentIds = Object.keys(this.cache.assignments);
        this.notifyAll();
        this.persist();
    }

    /** 鍵が総入れ替えになったときの通知。個別 id の購読者にも一斉に知らせる。 */
    private notifyAll(): void {
        this.slidesListeners.forEach((l) => l());
        this.assignmentIdsListeners.forEach((l) => l());
        this.assignmentsListeners.forEach((l) => l());
        this.slideListenersMap.forEach((set) => set.forEach((l) => l()));
        this.elementsListeners.forEach((l) => l());
        this.elementListenersMap.forEach((set) => set.forEach((l) => l()));
        this.assignmentListenersMap.forEach((set) => set.forEach((l) => l()));
    }

    // ---- スライド ----

    getSlideIds(): string[] {
        this.ensureLoaded();
        return this.cachedSlideIds;
    }

    getSlide(slideId: string): SlideSetting | undefined {
        this.ensureLoaded();
        return this.cache.slides[slideId];
    }

    getSlides(): SlideSettingMap {
        this.ensureLoaded();
        return this.cache.slides;
    }

    writeSlide(slideId: string, next: SlideSetting | null): void {
        this.ensureLoaded();
        if (this.readOnly) return;
        const existed = slideId in this.cache.slides;
        if (next === null) {
            if (!existed) return;
            const { [slideId]: _omit, ...rest } = this.cache.slides;
            this.cache = { ...this.cache, slides: rest };
        } else {
            if (this.cache.slides[slideId] === next) return;
            this.cache = { ...this.cache, slides: { ...this.cache.slides, [slideId]: next } };
        }
        if (existed !== (next !== null)) {
            this.cachedSlideIds = Object.keys(this.cache.slides);
        }
        this.slideListenersMap.get(slideId)?.forEach((l) => l());
        this.slidesListeners.forEach((l) => l());
        this.persist();
    }

    subscribeSlide(slideId: string, cb: () => void): () => void {
        this.ensureLoaded();
        return subscribeTo(this.slideListenersMap, slideId, cb);
    }

    subscribeSlides(cb: () => void): () => void {
        this.ensureLoaded();
        this.slidesListeners.add(cb);
        return () => {
            this.slidesListeners.delete(cb);
        };
    }

    // ---- 要素 ----

    getElements(): ElementSettingMap {
        this.ensureLoaded();
        return this.cache.elements;
    }

    getElement(creationId: string): ElementSetting | undefined {
        this.ensureLoaded();
        return this.cache.elements[creationId];
    }

    writeElement(creationId: string, next: ElementSetting | null): void {
        this.writeElements([[creationId, next]]);
    }

    writeElements(entries: ReadonlyArray<readonly [string, ElementSetting | null]>): void {
        this.ensureLoaded();
        if (this.readOnly) return;
        let elements = this.cache.elements;
        const touched: string[] = [];
        for (const [creationId, next] of entries) {
            if (next === null) {
                if (!(creationId in elements)) continue;
                const { [creationId]: _omit, ...rest } = elements;
                elements = rest;
            } else {
                if (elements[creationId] === next) continue;
                elements = { ...elements, [creationId]: next };
            }
            touched.push(creationId);
        }
        if (touched.length === 0) return;
        this.cache = { ...this.cache, elements };
        this.elementsListeners.forEach((l) => l());
        for (const creationId of touched) {
            this.elementListenersMap.get(creationId)?.forEach((l) => l());
        }
        this.persist();
    }

    subscribeElements(cb: () => void): () => void {
        this.ensureLoaded();
        this.elementsListeners.add(cb);
        return () => {
            this.elementsListeners.delete(cb);
        };
    }

    subscribeElement(creationId: string, cb: () => void): () => void {
        this.ensureLoaded();
        return subscribeTo(this.elementListenersMap, creationId, cb);
    }

    // ---- 生成担当 ----

    getAssignmentIds(): string[] {
        this.ensureLoaded();
        return this.cachedAssignmentIds;
    }

    getAssignment(id: string): Assignment | undefined {
        this.ensureLoaded();
        return this.cache.assignments[id];
    }

    getAssignments(): AssignmentMap {
        this.ensureLoaded();
        return this.cache.assignments;
    }

    writeAssignment(id: string, next: Assignment | null): void {
        this.writeAssignments([[id, next]]);
    }

    writeAssignments(entries: ReadonlyArray<readonly [string, Assignment | null]>): void {
        this.ensureLoaded();
        if (this.readOnly) return;
        let assignments = this.cache.assignments;
        const touched: string[] = [];
        let idsChanged = false;
        for (const [id, next] of entries) {
            const existed = id in assignments;
            if (next === null) {
                if (!existed) continue;
                const { [id]: _omit, ...rest } = assignments;
                assignments = rest;
                idsChanged = true;
            } else {
                // 下書きは「まだ何も書かれていない」ものだけ。ラベル・本文・対象のいずれかが
                // 入れば保存する。ラベルだけを条件にすると、本文を書いても保存されない状態が
                // 生まれる（実機の指摘）。掃除の目的は押しただけのノードを残さないことなので、
                // 中身が空かどうかで判定するほうが目的に合う。
                if (isAssignmentBlank(next)) {
                    if (!existed) continue;
                    const { [id]: _omit, ...rest } = assignments;
                    assignments = rest;
                    idsChanged = true;
                } else {
                    if (assignments[id] === next) continue;
                    assignments = { ...assignments, [id]: next };
                    if (!existed) idsChanged = true;
                }
            }
            touched.push(id);
        }
        if (touched.length === 0) return;
        this.cache = { ...this.cache, assignments };
        if (idsChanged) {
            this.cachedAssignmentIds = Object.keys(this.cache.assignments);
            this.assignmentIdsListeners.forEach((l) => l());
        }
        for (const id of touched) {
            this.assignmentListenersMap.get(id)?.forEach((l) => l());
        }
        this.assignmentsListeners.forEach((l) => l());
        this.persist();
    }

    subscribeAssignmentIds(cb: () => void): () => void {
        this.ensureLoaded();
        this.assignmentIdsListeners.add(cb);
        return () => {
            this.assignmentIdsListeners.delete(cb);
        };
    }

    subscribeAssignment(id: string, cb: () => void): () => void {
        this.ensureLoaded();
        return subscribeTo(this.assignmentListenersMap, id, cb);
    }

    subscribeAssignments(cb: () => void): () => void {
        this.ensureLoaded();
        this.assignmentsListeners.add(cb);
        return () => {
            this.assignmentsListeners.delete(cb);
        };
    }

    // ---- 生成元メタ ----

    getSourceTaskId(): string | null {
        this.ensureLoaded();
        return this._sourceTaskId;
    }

    getSourceProjectId(): string | null {
        this.ensureLoaded();
        return this._sourceProjectId;
    }

    /** 現在の内容を `im.prompts` ペイロードとして取り出す（永続形と同一）。 */
    getPayload(): SettingsPayload {
        this.ensureLoaded();
        return this.buildPayload();
    }

    // ---- test 用 ----

    __resetForTest(): void {
        this.cache = emptyCache();
        this.loaded = false;
        this.readOnly = false;
        this._sourceTaskId = null;
        this._sourceProjectId = null;
        this.cachedSlideIds = [];
        this.cachedAssignmentIds = [];
        this.slidesListeners.clear();
        this.slideListenersMap.clear();
        this.elementListenersMap.clear();
        this.assignmentIdsListeners.clear();
        this.assignmentsListeners.clear();
        this.assignmentListenersMap.clear();
    }

    // ---- private ----

    private ensureLoaded(): void {
        if (this.loaded) return;
        const stored = readSetting<Partial<SettingsPayload> & LegacyV6Payload>(SETTINGS_KEY);
        if (stored && typeof stored.version === "number") {
            if (stored.version > SETTINGS_PAYLOAD_VERSION) {
                // 将来版のデータを壊さないため、読み取りだけ許して書き込みを止める。
                this.readOnly = true;
                this.cache = {
                    slides: stored.slides ?? {},
                    elements: stored.elements ?? {},
                    assignments: stored.assignments ?? {},
                };
            } else if (stored.version === SETTINGS_PAYLOAD_VERSION) {
                this.cache = {
                    slides: stored.slides ?? {},
                    elements: stored.elements ?? {},
                    assignments: stored.assignments ?? {},
                };
            } else if (stored.version === 7) {
                // 鍵はまだ PowerPoint 発行の ID。対応表が届くまでこのまま持つ。
                this.version = 7;
                this.cache = {
                    slides: stored.slides ?? {},
                    elements: stored.elements ?? {},
                    assignments: stored.assignments ?? {},
                };
            } else {
                this.version = 7;
                const migrated = migrateV6(stored, new Date().toISOString());
                this.cache = {
                    slides: migrated.slides,
                    elements: migrated.elements,
                    assignments: migrated.assignments,
                };
            }
            this._sourceTaskId = stored.sourceTaskId ?? null;
            this._sourceProjectId = stored.sourceProjectId ?? null;
            this.finishLoad(stored.version < SETTINGS_PAYLOAD_VERSION);
            return;
        }
        const legacy = readSetting<{
            [shapeId: string]: {
                prompt: string;
                label?: string;
                updatedAt: string;
                updatedBy: string;
            };
        }>(LEGACY_KEY);
        if (legacy && Object.keys(legacy).length > 0) {
            this.version = 7;
            const migrated = migrateV6(legacyV1ToV6(legacy), new Date().toISOString());
            this.cache = {
                slides: migrated.slides,
                elements: migrated.elements,
                assignments: migrated.assignments,
            };
            this.finishLoad(true);
            // **読み替えた旧キーは落とす。** 残すと 1 つの pptx に同じ設定の古い写しと新しい
            // 正本が並び、外部用のスクラブや別環境での取り込みで拾われる余地が残る（記録 10 の
            // 申し送り）。新しい鍵を書いたあとに消す（`finishLoad(true)` が書いている）。
            removeAndQueueSave(LEGACY_KEY);
            return;
        }
        this.cache = emptyCache();
        this.finishLoad(false);
    }

    /** 読み込みを確定し、id キャッシュを張る。移行が起きたときだけ即座に書き戻す。 */
    private finishLoad(persistNow: boolean): void {
        // 読み込みの経路はすべてここへ集まるので、廃止した裁量の読み替えもここで一度だけ行う。
        this.cache = {
            ...this.cache,
            elements: dropInvalidMaxRowsBeforeNext(readAutoAsEmbed(this.cache.elements)),
        };
        this.loaded = true;
        this.cachedSlideIds = Object.keys(this.cache.slides);
        this.cachedAssignmentIds = Object.keys(this.cache.assignments);
        if (persistNow) this.persist();
    }

    private buildPayload(): SettingsPayload {
        const payload: SettingsPayload = {
            version: this.version,
            slides: this.cache.slides,
            elements: this.cache.elements,
            assignments: this.cache.assignments,
        };
        if (this._sourceTaskId) payload.sourceTaskId = this._sourceTaskId;
        if (this._sourceProjectId) payload.sourceProjectId = this._sourceProjectId;
        return payload;
    }

    private persist(): void {
        if (this.readOnly) return;
        writeAndQueueSave(SETTINGS_KEY, this.buildPayload());
    }
}

/** id ごとの listener 集合へ登録し、空になったらキーごと捨てる購読ヘルパー。 */
function subscribeTo(map: Map<string, Set<() => void>>, key: string, cb: () => void): () => void {
    let set = map.get(key);
    if (!set) {
        set = new Set();
        map.set(key, set);
    }
    set.add(cb);
    return () => {
        set.delete(cb);
        if (set.size === 0) map.delete(key);
    };
}

/** taskpane で使うシングルトン。 */
export const generationSettingStore = new SettingsGenerationSettingStore();
